import {
  applyMatrixToPoint,
  createMatrixFromArray,
  createMinimumRectangle,
  drawLabel,
  findRoomCorners,
  findRoomInterior,
} from './floorPlanUtils';
import { getDimensions, getCenter, getBounds } from './floorPlanDataHelpers';
import { hydrateRoomOpenings } from './openingSyncUtils';
import { isPointInsidePolygon } from '@shared-lib/geometry/pointInPolygon';
/**
 * Creates a polygon representation of a room
 * @param {Object} room - The room object
 * @param {Array} walls - Array of all walls in the floor plan
 * @param {Number} tolerance - Distance tolerance for connecting walls (in meters)
 * @returns {Object} Room polygon data including points, area, and centroid
 */
export const getRoomPolygon = (room, rotation, tolerance = 0.05) => {
  // Find corners for the room
  const { points, walls } = findRoomCorners(room, rotation, tolerance);

  // If we have at least 3 points, create a proper polygon
  if (points.length >= 3) {
    return createPolygonData(points, walls);
  }

  // Fallback: Create a minimum rectangle from room bounds or sections
  const fallbackPoints = getFallbackRoomPoints(room);

  // If there are no walls AND no usable section data (e.g. a room whose walls
  // have all been deleted by the user), there is no polygon to compute.
  if (fallbackPoints.length === 0) {
    return null;
  }

  const rectanglePoints = createMinimumRectangle(fallbackPoints, rotation || 0);
  return createPolygonData(rectanglePoints, []);
};

/**
 * Gets fallback points from room sections or bounds
 * @param {Object} room - The room object
 * @returns {Array} Array of points [x, z]
 */
const getFallbackRoomPoints = (room) => {
  const points = [];

  // Try to get points from room sections
  if (room.sections && room.sections.length > 0) {
    room.sections.forEach((section) => {
      if (section.bounds) {
        // Extract corner points from bounds [minX, minY, minZ, maxX, maxY, maxZ]
        let minX, minY, minZ, maxX, maxY, maxZ;
        if (Array.isArray(section.bounds)) {
          [minX, minY, minZ, maxX, maxY, maxZ] = section.bounds;
        } else if (section.bounds.min && section.bounds.max) {
          minX = section.bounds.min.x || section.bounds.minX;
          minY = section.bounds.min.y || section.bounds.minY;
          minZ = section.bounds.min.z || section.bounds.minZ;
          maxX = section.bounds.max.x || section.bounds.maxX;
          maxY = section.bounds.max.y || section.bounds.maxY;
          maxZ = section.bounds.max.z || section.bounds.maxZ;
        } else {
          minX = section.bounds.minX;
          minY = section.bounds.minY;
          minZ = section.bounds.minZ;
          maxX = section.bounds.maxX;
          maxY = section.bounds.maxY;
          maxZ = section.bounds.maxZ;
        }
        points.push(
          [minX, minZ], // bottom-left
          [maxX, minZ], // bottom-right
          [maxX, maxZ], // top-right
          [minX, maxZ] // top-left
        );
      } else if (section.center && section.dimensions) {
        // Create points from center and dimensions
        let centerX, centerY, centerZ;
        if (Array.isArray(section.center)) {
          [centerX, centerY, centerZ] = section.center;
        } else {
          centerX = section.center.x;
          centerY = section.center.y;
          centerZ = section.center.z;
        }

        let width, height, depth;
        if (Array.isArray(section.dimensions)) {
          [width, height, depth] = section.dimensions;
        } else {
          width = section.dimensions.x || section.dimensions.width;
          height = section.dimensions.y || section.dimensions.height;
          depth = section.dimensions.z || section.dimensions.depth;
        }

        const halfWidth = width / 2;
        const halfDepth = depth / 2;

        points.push(
          [centerX - halfWidth, centerZ - halfDepth],
          [centerX + halfWidth, centerZ - halfDepth],
          [centerX + halfWidth, centerZ + halfDepth],
          [centerX - halfWidth, centerZ + halfDepth]
        );
      } else if (section.center) {
        // Just use the center point (will need at least 3 for rectangle)
        let centerX, centerY, centerZ;
        if (Array.isArray(section.center)) {
          [centerX, centerY, centerZ] = section.center;
        } else {
          centerX = section.center.x;
          centerY = section.center.y;
          centerZ = section.center.z;
        }
        points.push([centerX, centerZ]);
      }
    });
  }

  // Remove duplicate points
  const uniquePoints = [];
  const tolerance = 0.01;

  points.forEach((point) => {
    const isDuplicate = uniquePoints.some(
      (existing) =>
        Math.abs(existing[0] - point[0]) < tolerance &&
        Math.abs(existing[1] - point[1]) < tolerance
    );

    if (!isDuplicate) {
      uniquePoints.push(point);
    }
  });

  return uniquePoints;
};

/**
 * Creates polygon data including points, area, and centroid
 * @param {Array} points - Array of polygon points [x, z]
 * @returns {Object} Polygon data
 */
const createPolygonData = (points, walls) => {
  const area = calculatePolygonArea(points);
  const centroid = calculatePolygonCentroid(points);
  const boundingBox = calculateBoundingBox(points);

  return {
    walls,
    points,
    area,
    centroid,
    boundingBox,
  };
};

/**
 * Calculates the area of a polygon
 * @param {Array} points - Array of polygon points [x, z]
 * @returns {Number} Area in square meters
 */
export const calculatePolygonArea = (points) => {
  if (points.length < 3) return 0;

  let area = 0;

  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    area += (points[j][0] + points[i][0]) * (points[j][1] - points[i][1]);
  }

  return Math.abs(area / 2);
};

/**
 * Calculates the centroid (geometric center) of a polygon
 * @param {Array} points - Array of polygon points [x, z]
 * @returns {Array} Centroid point [x, z]
 */
export const calculatePolygonCentroid = (points) => {
  if (points.length < 3) return [0, 0];

  let area = 0;
  let cx = 0;
  let cz = 0;

  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const cross = points[j][0] * points[i][1] - points[i][0] * points[j][1];
    area += cross;
    cx += (points[j][0] + points[i][0]) * cross;
    cz += (points[j][1] + points[i][1]) * cross;
  }

  area = area / 2;

  // If area is near zero, return average of points
  if (Math.abs(area) < 0.000001) {
    const sumX = points.reduce((sum, p) => sum + p[0], 0);
    const sumZ = points.reduce((sum, p) => sum + p[1], 0);
    return [sumX / points.length, sumZ / points.length];
  }

  // Finalize the centroid coordinates
  cx = cx / (6 * area);
  cz = cz / (6 * area);

  return [cx, cz];
};

/**
 * Calculates the bounding box of a polygon
 * @param {Array} points - Array of polygon points [x, z]
 * @returns {Object} Bounding box with minX, minZ, maxX, maxZ
 */
export const calculateBoundingBox = (points) => {
  if (points.length === 0) {
    return { minX: 0, minZ: 0, maxX: 0, maxZ: 0 };
  }

  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;

  points.forEach((point) => {
    const [x, z] = point;
    minX = Math.min(minX, x);
    minZ = Math.min(minZ, z);
    maxX = Math.max(maxX, x);
    maxZ = Math.max(maxZ, z);
  });

  return { minX, minZ, maxX, maxZ };
};

/**
 * Checks if a point is inside a polygon
 * @param {Array} point - Point coordinates [x, z]
 * @param {Array} polygonPoints - Array of polygon points [x, z]
 * @returns {Boolean} True if point is inside the polygon
 */
export const isPointInPolygon = (point, polygonPoints) => {
  if (polygonPoints.length < 3) return false;

  const [x, z] = point;
  let inside = false;

  for (
    let i = 0, j = polygonPoints.length - 1;
    i < polygonPoints.length;
    j = i++
  ) {
    const [xi, zi] = polygonPoints[i];
    const [xj, zj] = polygonPoints[j];

    const intersect =
      zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
};

/**
 * Gets all polygons for rooms in a floor plan
 * @param {Object} data - The floor plan data
 * @returns {Object} Map of room identifiers to polygon data
 */
export const getAllRoomPolygons = (data, rotation) => {
  const polygons = {};

  data.rooms.forEach((room) => {
    if (room.id) {
      const polygon = getRoomPolygon(room, rotation);
      if (polygon) {
        polygons[room.id] = polygon;
      }
    }
  });

  return polygons;
};

// SHOWER PLACEMENT FUNCTIONALITY

// Transform polygon corners using a transform matrix
export function transformPolygonCorners(corners, transform) {
  if (!transform || transform.length !== 16) {
    return corners;
  }

  // Check if this is just a translation (identity rotation)
  const isIdentityRotation =
    Math.abs(transform[0] - 1) < 0.001 &&
    Math.abs(transform[5] - 1) < 0.001 &&
    Math.abs(transform[10] - 1) < 0.001 &&
    Math.abs(transform[1]) < 0.001 &&
    Math.abs(transform[2]) < 0.001 &&
    Math.abs(transform[4]) < 0.001 &&
    Math.abs(transform[6]) < 0.001 &&
    Math.abs(transform[8]) < 0.001 &&
    Math.abs(transform[9]) < 0.001;

  if (isIdentityRotation) {
    const offsetX = transform[12];
    const offsetY = transform[13];
    const offsetZ = transform[14];

    return corners.map((corner) => ({
      x: corner.x + offsetX,
      y: corner.y + offsetY,
      z: (corner.z || 0) + offsetZ,
    }));
  }

  return corners.map((corner, index) => {
    // Apply 4x4 transform matrix to the point
    const x = corner.x;
    const y = corner.y;
    const z = corner.z || 0;

    // Matrix multiplication
    const newX =
      transform[0] * x + transform[4] * y + transform[8] * z + transform[12];
    const newY =
      transform[1] * x + transform[5] * y + transform[9] * z + transform[13];
    const newZ =
      transform[2] * x + transform[6] * y + transform[10] * z + transform[14];

    return { x: newX, y: newY, z: newZ };
  });
}

/**
 * Compute the inward-pointing unit normal for a polygon edge using winding order.
 * Works correctly for both convex and concave polygons.
 *
 * In the XZ plane (Y-up), the perpendicular directions are swapped vs standard XY:
 * For a CCW polygon (positive signed area), the inward normal of edge A→B
 * is the right perpendicular: (dz, -dx) normalized.
 * For a CW polygon (negative signed area), it's (-dz, dx) normalized.
 *
 * @param {Array<{x: number, z: number}>} vertices - Polygon vertices
 * @param {number} edgeIndex - Index of the edge (edge goes from vertices[edgeIndex] to vertices[(edgeIndex+1) % n])
 * @returns {{x: number, z: number}} Unit vector pointing into the room interior
 */
export function getEdgeInwardNormal(vertices, edgeIndex) {
  const n = vertices.length;
  const a = vertices[edgeIndex];
  const b = vertices[(edgeIndex + 1) % n];
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const length = Math.sqrt(dx * dx + dz * dz);
  if (length < 1e-12) return { x: 0, z: 0 };

  const isCW = isPolygonClockwise(vertices);

  if (isCW) {
    // CW in XZ plane: inward = (-dz, dx) / length
    return { x: -dz / length, z: dx / length };
  } else {
    // CCW in XZ plane: inward = (dz, -dx) / length
    return { x: dz / length, z: -dx / length };
  }
}

/**
 * Compute the outward-pointing unit normal for a polygon edge.
 * Convenience wrapper that negates getEdgeInwardNormal.
 *
 * @param {Array<{x: number, z: number}>} vertices - Polygon vertices
 * @param {number} edgeIndex - Index of the edge
 * @returns {{x: number, z: number}} Unit vector pointing away from the room interior
 */
export function getEdgeOutwardNormal(vertices, edgeIndex) {
  const inward = getEdgeInwardNormal(vertices, edgeIndex);
  return { x: -inward.x, z: -inward.z };
}

// Determine if polygon is clockwise or counterclockwise
// For floor polygons in XZ plane (Y is up)
export function isPolygonClockwise(polygonCorners) {
  // Calculate the signed area using the shoelace formula in XZ plane
  let signedArea = 0;
  const n = polygonCorners.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    // Use X and Z coordinates (not Y which is vertical)
    signedArea +=
      (polygonCorners[j].x - polygonCorners[i].x) *
      (polygonCorners[j].z + polygonCorners[i].z);
  }

  // When viewed from above (positive Y):
  // Negative area means clockwise, positive means counterclockwise
  return signedArea < 0;
}

// Calculate interior angle at a vertex (in degrees)
// For floor polygons in XZ plane
export function calculateInteriorAngle(p1, p2, p3, isClockwise = false) {
  // p2 is the vertex, p1 and p3 are adjacent points
  // For floor polygons, we use X and Z coordinates (Y is vertical)
  const v1 = { x: p1.x - p2.x, z: p1.z - p2.z };
  const v2 = { x: p3.x - p2.x, z: p3.z - p2.z };

  // Calculate angle using dot product in XZ plane
  const dotProduct = v1.x * v2.x + v1.z * v2.z;
  const mag1 = Math.sqrt(v1.x * v1.x + v1.z * v1.z);
  const mag2 = Math.sqrt(v2.x * v2.x + v2.z * v2.z);

  if (mag1 === 0 || mag2 === 0) return 0;

  const cosAngle = dotProduct / (mag1 * mag2);
  // Clamp to prevent Math.acos errors
  const clampedCos = Math.max(-1, Math.min(1, cosAngle));
  const angleRad = Math.acos(clampedCos);
  const angleDeg = angleRad * (180 / Math.PI);

  // Cross product in XZ plane (Y component of 3D cross product)
  // v1 × v2 = v1.x * v2.z - v1.z * v2.x
  const cross = v1.x * v2.z - v1.z * v2.x;

  // Interior angle calculation depends on polygon winding order:
  // For COUNTERCLOCKWISE polygons (when viewed from above):
  //   - Positive cross product = interior angle is the calculated angle
  //   - Negative cross product = interior angle is 360 - calculated angle
  // For CLOCKWISE polygons:
  //   - Negative cross product = interior angle is the calculated angle
  //   - Positive cross product = interior angle is 360 - calculated angle

  let interiorAngle;
  if (isClockwise) {
    interiorAngle = cross < 0 ? angleDeg : 360 - angleDeg;
  } else {
    interiorAngle = cross > 0 ? angleDeg : 360 - angleDeg;
  }

  return interiorAngle;
}

// Get wall vectors at a corner
export function getWallVectors(cornerIndex, polygonCorners) {
  const numCorners = polygonCorners.length;
  const prevIndex = (cornerIndex - 1 + numCorners) % numCorners;
  const nextIndex = (cornerIndex + 1) % numCorners;

  const corner = polygonCorners[cornerIndex];
  const prevCorner = polygonCorners[prevIndex];
  const nextCorner = polygonCorners[nextIndex];

  // Incoming wall vector (from previous corner to current)
  const incomingVector = {
    x: corner.x - prevCorner.x,
    y: corner.y - prevCorner.y,
    z: corner.z - prevCorner.z || 0,
  };

  // Outgoing wall vector (from current to next corner)
  const outgoingVector = {
    x: nextCorner.x - corner.x,
    y: nextCorner.y - corner.y,
    z: nextCorner.z - corner.z || 0,
  };

  return { incomingVector, outgoingVector };
}

// Check if walls are long enough for shower
export function checkWallLengths(
  corner,
  incomingVector,
  outgoingVector,
  minLength = 0.762
) {
  // Calculate wall lengths
  const incomingLength = Math.sqrt(
    incomingVector.x ** 2 + incomingVector.y ** 2 + incomingVector.z ** 2
  );

  const outgoingLength = Math.sqrt(
    outgoingVector.x ** 2 + outgoingVector.y ** 2 + outgoingVector.z ** 2
  );

  return {
    valid: incomingLength >= minLength && outgoingLength >= minLength,
    incomingLength,
    outgoingLength,
  };
}

// Generate shower footprint polygon
export function generateShowerFootprint(
  corner,
  incomingVector,
  outgoingVector,
  showerDimensions = null
) {
  // Normalize vectors in XZ plane only (floor plane)
  const normalizeVector = (v) => {
    const mag = Math.sqrt(v.x * v.x + v.z * v.z);
    if (mag === 0) return { x: 0, z: 0 };
    return { x: v.x / mag, z: v.z / mag };
  };

  const normIncoming = normalizeVector(incomingVector);
  const normOutgoing = normalizeVector(outgoingVector);

  // Calculate shower corners using actual dimensions
  const showerWidth = showerDimensions?.width || 0.762; // Default 30 inches in meters
  const showerDepth = showerDimensions?.depth || 0.762; // Default 30 inches in meters

  // For the shower footprint, we need to go BACK along the incoming wall
  // and FORWARD along the outgoing wall
  const showerCorners = [
    corner, // Corner point
    {
      x: corner.x - normIncoming.x * showerWidth, // Back along incoming wall
      y: corner.y,
      z: corner.z - normIncoming.z * showerWidth,
    }, // Point along incoming wall
    {
      x: corner.x - normIncoming.x * showerWidth + normOutgoing.x * showerDepth,
      y: corner.y,
      z: corner.z - normIncoming.z * showerWidth + normOutgoing.z * showerDepth,
    }, // Opposite corner of shower
    {
      x: corner.x + normOutgoing.x * showerDepth, // Forward along outgoing wall
      y: corner.y,
      z: corner.z + normOutgoing.z * showerDepth,
    }, // Point along outgoing wall
  ];

  return showerCorners;
}

// Calculate shower rotation based on wall vectors
export function calculateShowerRotation(incomingVector, outgoingVector) {
  // For a corner shower, we want it to fit flush in the corner
  // The shower's local axes need to align with the room's walls:
  // - Shower's X-axis (width) should align with the incoming wall
  // - Shower's Z-axis (depth) should align with the outgoing wall

  // Normalize the vectors first
  const normalizeVector = (v) => {
    const mag = Math.sqrt(v.x * v.x + v.z * v.z); // Use XZ plane
    if (mag === 0) return { x: 0, z: 0 };
    return { x: v.x / mag, z: v.z / mag };
  };

  const normIncoming = normalizeVector(incomingVector);
  const normOutgoing = normalizeVector(outgoingVector);

  // Calculate the angle that aligns the shower properly in the corner
  // The shower model typically has its back facing -Z in local space
  // We want the -Z direction to point toward the corner (opposite of outgoing)

  // Get the angle of the outgoing wall vector
  const outgoingAngle = Math.atan2(normOutgoing.z, normOutgoing.x);

  // The shower's local -Z should point opposite to the outgoing vector
  // So we need to rotate the shower to align its Z axis with the outgoing vector
  const rotation = outgoingAngle;

  return rotation;
}

// Get object bounds for collision detection
export function getObjectBounds(obj) {
  // Get object position from transform matrix if available
  let position = { x: 0, y: 0, z: 0 };

  if (obj.transform && obj.transform.length === 16) {
    // Extract position from transform matrix (last column)
    position = {
      x: obj.transform[12],
      y: obj.transform[13],
      z: obj.transform[14],
    };
  } else if (obj.position) {
    position = obj.position;
  } else if (obj.center) {
    // Some objects might have center instead of position
    position = {
      x: obj.center[0] || 0,
      y: obj.center[1] || 0,
      z: obj.center[2] || 0,
    };
  }

  // Get dimensions - handle various formats
  let width = 1,
    depth = 1;

  if (Array.isArray(obj.dimensions)) {
    // Dimensions as [width, height, depth] in meters
    width = obj.dimensions[0] || 1;
    depth = obj.dimensions[2] || 1;
  } else if (obj.dimensions) {
    // Dimensions as object with width/depth properties
    width = obj.dimensions.width || 1;
    depth = obj.dimensions.depth || 1;

    // Convert from inches to meters if needed
    if (obj.dimensions.unit === 'inches') {
      width *= 0.0254;
      depth *= 0.0254;
    }
  } else if (obj.bounds) {
    // Some objects store bounds directly
    const bounds = obj.bounds;
    if (Array.isArray(bounds) && bounds.length >= 6) {
      // Bounds as [minX, minY, minZ, maxX, maxY, maxZ]
      width = Math.abs(bounds[3] - bounds[0]);
      depth = Math.abs(bounds[5] - bounds[2]);
      position = {
        x: (bounds[0] + bounds[3]) / 2,
        y: (bounds[1] + bounds[4]) / 2,
        z: (bounds[2] + bounds[5]) / 2,
      };
    }
  }

  // Apply rotation if present to get accurate bounds
  let corners;
  if (obj.rotation && (obj.rotation.y || obj.rotation[1])) {
    // Get rotation angle in radians
    const rotY = obj.rotation.y || obj.rotation[1] || 0;
    const cos = Math.cos(rotY);
    const sin = Math.sin(rotY);

    const halfWidth = width / 2;
    const halfDepth = depth / 2;

    // Rotate the corners
    corners = [
      {
        x: position.x + (cos * -halfWidth - sin * -halfDepth),
        z: position.z + (sin * -halfWidth + cos * -halfDepth),
      },
      {
        x: position.x + (cos * halfWidth - sin * -halfDepth),
        z: position.z + (sin * halfWidth + cos * -halfDepth),
      },
      {
        x: position.x + (cos * halfWidth - sin * halfDepth),
        z: position.z + (sin * halfWidth + cos * halfDepth),
      },
      {
        x: position.x + (cos * -halfWidth - sin * halfDepth),
        z: position.z + (sin * -halfWidth + cos * halfDepth),
      },
    ];
  } else {
    // No rotation, simple rectangular bounds
    const halfWidth = width / 2;
    const halfDepth = depth / 2;

    corners = [
      { x: position.x - halfWidth, z: position.z - halfDepth },
      { x: position.x + halfWidth, z: position.z - halfDepth },
      { x: position.x + halfWidth, z: position.z + halfDepth },
      { x: position.x - halfWidth, z: position.z + halfDepth },
    ];
  }

  // Return bounds in XZ plane (floor plane)
  return corners;
}

// Check if a point is inside a polygon (using ray casting algorithm)
const isPointInPolygon2D = isPointInsidePolygon;

// Check if two polygons overlap
// For floor plan objects, we check overlap in XZ plane
export function polygonsOverlap(poly1, poly2) {
  // First, quick bounding box check
  const getBounds = (poly) => {
    const xs = poly.map((p) => p.x);
    const zs = poly.map((p) => (p.z !== undefined ? p.z : p.y)); // Handle both XZ and XY formats
    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minZ: Math.min(...zs),
      maxZ: Math.max(...zs),
    };
  };

  const bounds1 = getBounds(poly1);
  const bounds2 = getBounds(poly2);

  // If bounding boxes don't overlap, polygons can't overlap
  const boundsOverlap = !(
    bounds1.maxX < bounds2.minX ||
    bounds1.minX > bounds2.maxX ||
    bounds1.maxZ < bounds2.minZ ||
    bounds1.minZ > bounds2.maxZ
  );

  if (!boundsOverlap) {
    return false;
  }

  // Now do more precise check:
  // 1. Check if any vertex of poly1 is inside poly2
  for (const vertex of poly1) {
    if (isPointInPolygon2D(vertex, poly2)) {
      return true;
    }
  }

  // 2. Check if any vertex of poly2 is inside poly1
  for (const vertex of poly2) {
    if (isPointInPolygon2D(vertex, poly1)) {
      return true;
    }
  }

  // 3. Check for edge intersections
  // This catches cases where polygons overlap but no vertices are inside
  for (let i = 0; i < poly1.length; i++) {
    const a1 = poly1[i];
    const a2 = poly1[(i + 1) % poly1.length];

    for (let j = 0; j < poly2.length; j++) {
      const b1 = poly2[j];
      const b2 = poly2[(j + 1) % poly2.length];

      if (doLineSegmentsIntersect(a1, a2, b1, b2)) {
        return true;
      }
    }
  }

  return false;
}

// Check if two line segments intersect
export function doLineSegmentsIntersect(a1, a2, b1, b2) {
  const det = (a2.x - a1.x) * (b2.z - b1.z) - (a2.z - a1.z) * (b2.x - b1.x);

  if (Math.abs(det) < 0.0001) {
    // Lines are parallel
    return false;
  }

  const lambda =
    ((b2.z - b1.z) * (b2.x - a1.x) + (b1.x - b2.x) * (b2.z - a1.z)) / det;
  const gamma =
    ((a1.z - a2.z) * (b2.x - a1.x) + (a2.x - a1.x) * (b2.z - a1.z)) / det;

  return 0 < lambda && lambda < 1 && 0 < gamma && gamma < 1;
}

// Check for object collisions
export function checkObjectCollisions(showerFootprint, room) {
  const objects = room.objects || [];

  for (const obj of objects) {
    // Skip checking against other showers we might have already placed
    // Corner showers are type 'shower' with wallCount >= 2
    if (obj.type === 'shower' && obj.wallCount >= 2) continue;

    // Get object bounds
    const objBounds = getObjectBounds(obj);

    // Check if shower footprint overlaps with object bounds
    if (polygonsOverlap(showerFootprint, objBounds)) {
      return {
        hasCollision: true,
        collidingObject: obj,
      };
    }
  }

  return { hasCollision: false };
}

// Check for conflicts with door/window openings.
// `room.doors` may be slim post-PR #634 — callers should pre-hydrate the
// room via `hydrateRoomOpenings` so each door has `center / dimensions`.
// Reads `door.center` (modern canonical field); legacy `door.position` and
// `door.transform[12..14]` are still tolerated as fallbacks for unmigrated
// data.
export function checkOpeningConflicts(showerFootprint, wallIndices, room) {
  const walls = room.walls || [];
  const conflicts = [];

  // Check if room.doors array exists and if any doors actually conflict with shower placement
  if (room.doors && room.doors.length > 0) {
    // Check each door's position to see if it actually conflicts
    for (const door of room.doors) {
      if (door.center || door.position || door.transform) {
        const doorPos =
          door.center ||
          door.position ||
          (door.transform && {
            x: door.transform[12],
            y: door.transform[13],
            z: door.transform[14],
          });

        if (doorPos) {
          // Check if door position is within shower footprint
          if (isPointInPolygon2D(doorPos, showerFootprint)) {
            conflicts.push({
              type: 'door',
              wallIndex: -1,
              opening: door,
            });
          } else {
            // Check distance from door to shower footprint
            const minDist = getMinDistanceToPolygon(doorPos, showerFootprint);
            if (minDist < 0.3) {
              // 30cm clearance
              conflicts.push({
                type: 'door',
                wallIndex: -1,
                opening: door,
              });
            }
          }
        }
      }
    }
  }

  // Check wall openings
  for (const wallIndex of wallIndices) {
    const wall = walls[wallIndex];
    if (!wall) {
      continue;
    }

    if (!wall.openings || wall.openings.length === 0) {
      continue;
    }

    for (const opening of wall.openings) {
      // Only check doors, not windows
      if (
        opening.type === 'door' ||
        opening.category === 'door' ||
        (opening.category &&
          typeof opening.category === 'object' &&
          opening.category.door)
      ) {
        // Check if the door is actually near the corner where shower would be placed
        // Get opening position along the wall
        if (opening.position !== undefined || opening.transform) {
          const openingPos =
            opening.position ||
            (opening.transform && {
              x: opening.transform[12],
              y: opening.transform[13],
              z: opening.transform[14],
            });

          if (openingPos) {
            // Check if opening overlaps with shower footprint
            const openingWidth = opening.dimensions
              ? opening.dimensions[0] || opening.dimensions.width || 0.9
              : 0.9;

            // Create a simple bounding box for the opening
            const openingBounds = createOpeningBounds(
              openingPos,
              openingWidth,
              wall
            );

            if (polygonsOverlap(showerFootprint, openingBounds)) {
              conflicts.push({
                type: 'door',
                wallIndex,
                opening,
              });
            }
          } else {
            // If we can't determine position, be conservative and flag as conflict
            conflicts.push({
              type: 'door',
              wallIndex,
              opening,
            });
          }
        }
      }
    }
  }

  return conflicts;
}

// Helper function to get minimum distance from point to polygon
function getMinDistanceToPolygon(point, polygon) {
  let minDist = Infinity;

  for (let i = 0; i < polygon.length; i++) {
    const p1 = polygon[i];
    const p2 = polygon[(i + 1) % polygon.length];

    // Distance from point to line segment
    const dist = distanceToLineSegment(point, p1, p2);
    minDist = Math.min(minDist, dist);
  }

  return minDist;
}

// Helper function to calculate distance from point to line segment
function distanceToLineSegment(point, lineStart, lineEnd) {
  const dx = lineEnd.x - lineStart.x;
  const dz = lineEnd.z - lineStart.z;
  const lengthSquared = dx * dx + dz * dz;

  if (lengthSquared === 0) {
    // Line segment is a point
    return Math.sqrt(
      (point.x - lineStart.x) ** 2 + (point.z - lineStart.z) ** 2
    );
  }

  // Parameter t determines where on the line segment the closest point is
  let t =
    ((point.x - lineStart.x) * dx + (point.z - lineStart.z) * dz) /
    lengthSquared;
  t = Math.max(0, Math.min(1, t));

  const closestPoint = {
    x: lineStart.x + t * dx,
    z: lineStart.z + t * dz,
  };

  return Math.sqrt(
    (point.x - closestPoint.x) ** 2 + (point.z - closestPoint.z) ** 2
  );
}

// Helper function to create bounds for an opening
function createOpeningBounds(position, width, wall) {
  // Simplified bounds - just create a small box around the opening position
  const halfWidth = width / 2;
  const depth = 0.3; // 30cm depth for door area

  return [
    { x: position.x - halfWidth, z: position.z - depth / 2 },
    { x: position.x + halfWidth, z: position.z - depth / 2 },
    { x: position.x + halfWidth, z: position.z + depth / 2 },
    { x: position.x - halfWidth, z: position.z + depth / 2 },
  ];
}

// Check ceiling height
export function checkCeilingHeight(corner, room, minHeight = 2.44) {
  // Get ceiling height at corner position
  const ceilingHeight = room.ceilings?.[0]?.height || room.height || 3.0;

  return ceilingHeight >= minHeight;
}

// Complete validation function
export function canPlaceShower(
  cornerIndex,
  room,
  transformedCorners,
  showerDimensions = null
) {
  const corner = transformedCorners[cornerIndex];
  const { incomingVector, outgoingVector } = getWallVectors(
    cornerIndex,
    transformedCorners
  );

  // 1. Check wall lengths
  // Use actual shower dimensions or default to 30"
  const requiredWallLength = Math.max(
    showerDimensions?.width || 0.762,
    showerDimensions?.depth || 0.762
  );
  const wallCheck = checkWallLengths(
    corner,
    incomingVector,
    outgoingVector,
    requiredWallLength
  );
  if (!wallCheck.valid) {
    return {
      valid: false,
      reason: 'Insufficient wall length',
      details: wallCheck,
    };
  }

  // 2. Generate shower footprint
  const showerFootprint = generateShowerFootprint(
    corner,
    incomingVector,
    outgoingVector,
    showerDimensions
  );

  // 3. Check object collisions
  const collisionCheck = checkObjectCollisions(showerFootprint, room);
  if (collisionCheck.hasCollision) {
    return {
      valid: false,
      reason: 'Object collision',
      details: collisionCheck,
    };
  }

  // 4. Check opening conflicts
  const wallIndices = [
    cornerIndex,
    (cornerIndex - 1 + room.walls.length) % room.walls.length,
  ];
  const openingConflicts = checkOpeningConflicts(
    showerFootprint,
    wallIndices,
    room
  );
  if (openingConflicts.length > 0) {
    return {
      valid: false,
      reason: 'Opening conflict',
      details: openingConflicts,
    };
  }

  // 5. Check ceiling height
  if (!checkCeilingHeight(corner, room)) {
    return {
      valid: false,
      reason: 'Insufficient ceiling height',
    };
  }

  return {
    valid: true,
    showerFootprint,
    wallIndices,
    rotation: calculateShowerRotation(incomingVector, outgoingVector),
    incomingVector,
    outgoingVector,
  };
}

// Find all valid shower locations in a room.
// `structureOpenings` is `{doors, windows, openings}` from the structure-
// level canonical arrays. When provided, the room is hydrated so the
// opening-conflict check sees real geometry for slim room copies.
export function findShowerLocations(
  room,
  scene = null,
  createDebugVisuals = false,
  showerDimensions = null,
  structureOpenings = null
) {
  if (!room.floors?.[0]?.polygonCorners) {
    return [];
  }

  const hydratedRoom = structureOpenings
    ? hydrateRoomOpenings(room, structureOpenings)
    : room;
  const floor = hydratedRoom.floors[0];

  // Transform polygon corners from local to world coordinates
  const polygonCorners = transformPolygonCorners(
    floor.polygonCorners,
    floor.transform
  );

  // Detect polygon winding order
  const isClockwise = isPolygonClockwise(polygonCorners);

  const numCorners = polygonCorners.length;
  const validLocations = [];
  const debugInfo = [];

  for (let i = 0; i < numCorners; i++) {
    const prevIndex = (i - 1 + numCorners) % numCorners;
    const nextIndex = (i + 1) % numCorners;

    // Calculate interior angle
    const angle = calculateInteriorAngle(
      polygonCorners[prevIndex],
      polygonCorners[i],
      polygonCorners[nextIndex],
      isClockwise
    );

    // Check if angle is approximately 90 degrees (85-95)
    if (angle >= 85 && angle <= 95) {
      // Validate shower placement — pass the hydrated room so the
      // opening-conflict check sees real door positions.
      const validation = canPlaceShower(
        i,
        hydratedRoom,
        polygonCorners,
        showerDimensions
      );

      if (validation.valid) {
        validLocations.push({
          cornerIndex: i,
          corner: polygonCorners[i],
          angle,
          ...validation,
        });

        // Store debug info with wall vectors
        debugInfo.push({
          cornerIndex: i,
          corner: polygonCorners[i],
          angle,
          isValid: true,
          reason: 'Valid for shower placement',
          wallVectors: getWallVectors(i, polygonCorners),
        });
      } else {
        // Store debug info with wall vectors
        debugInfo.push({
          cornerIndex: i,
          corner: polygonCorners[i],
          angle,
          isValid: false,
          reason: validation.reason,
          wallVectors: getWallVectors(i, polygonCorners),
        });
      }
    } else {
      // Store debug info with wall vectors
      debugInfo.push({
        cornerIndex: i,
        corner: polygonCorners[i],
        angle,
        isValid: false,
        reason: `Angle ${angle.toFixed(1)}° outside 85-95° range`,
        wallVectors: getWallVectors(i, polygonCorners),
      });
    }
  }

  // Return debug info along with valid locations
  return { validLocations, debugInfo };
}

// Generate unique ID for shower
export function generateShowerId() {
  return `shower_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Create shower object
export function createShowerObject(location, room, showerDimensions = null) {
  const corner = location.corner;
  const showerId = generateShowerId();

  // The corner.y already includes the floor height since we transformed the corners
  // So we should use corner.y as the floor level
  const floorHeight = corner.y;

  // Position shower at floor level (not below it)
  // The position in Three.js is the center of the object, so we need to offset by half height
  const showerWidth = showerDimensions?.width || 0.762; // Default 30 inches in meters
  const showerHeight = showerDimensions?.height || 2.44; // Default 96 inches in meters
  const showerDepth = showerDimensions?.depth || 0.762; // Default 30 inches in meters

  // Get the wall vectors to know which direction to offset
  const { incomingVector, outgoingVector } = location;

  // Normalize vectors to get unit directions
  const normalizeVector = (v) => {
    const mag = Math.sqrt(v.x * v.x + v.z * v.z);
    if (mag === 0) return { x: 0, z: 0 };
    return { x: v.x / mag, z: v.z / mag };
  };

  const normIncoming = normalizeVector(incomingVector);
  const normOutgoing = normalizeVector(outgoingVector);

  // Move the shower center away from the corner:
  // - Half width along the negative incoming vector (into the room)
  // - Half depth along the positive outgoing vector (into the room)
  const showerPosition = {
    x:
      corner.x -
      normIncoming.x * (showerWidth / 2) +
      normOutgoing.x * (showerDepth / 2),
    y: floorHeight + showerHeight / 2, // Center of shower (bottom at floor level)
    z:
      corner.z -
      normIncoming.z * (showerWidth / 2) +
      normOutgoing.z * (showerDepth / 2),
  };

  return {
    id: showerId,
    type: 'shower',
    category: 'shower',
    wallCount: 2, // Corner shower = 2 attached walls
    position: showerPosition,
    rotation: {
      x: 0,
      y: location.rotation,
      z: 0,
    },
    dimensions: [showerWidth, showerHeight, showerDepth], // Use actual dimensions
    wallIndices: location.wallIndices,
    material: 'default-shower-material',
    cornerIndex: location.cornerIndex,
    generatedByCompleteScan: true,
    transform: createTransformMatrix(showerPosition, location.rotation),
  };
}

// Helper to create transform matrix
function createTransformMatrix(position, rotationY) {
  // Create a 4x4 transform matrix
  const matrix = new Array(16);

  // rotation is already in radians from calculateShowerRotation
  const cosY = Math.cos(rotationY || 0);
  const sinY = Math.sin(rotationY || 0);

  // Build rotation matrix for Y-axis rotation
  matrix[0] = cosY; // m00
  matrix[1] = 0; // m01
  matrix[2] = sinY; // m02
  matrix[3] = 0; // m03

  matrix[4] = 0; // m10
  matrix[5] = 1; // m11
  matrix[6] = 0; // m12
  matrix[7] = 0; // m13

  matrix[8] = -sinY; // m20
  matrix[9] = 0; // m21
  matrix[10] = cosY; // m22
  matrix[11] = 0; // m23

  matrix[12] = position.x || 0; // m30 (x translation)
  matrix[13] = position.y || 0; // m31 (y translation)
  matrix[14] = position.z || 0; // m32 (z translation)
  matrix[15] = 1; // m33

  return matrix;
}

// SHOWER WALL TILING CALCULATION

/**
 * Calculate distance between two points in XZ plane
 */
function distance2D(p1, p2) {
  const dx = p2.x - p1.x;
  const dz = p2.z - p1.z;
  return Math.sqrt(dx * dx + dz * dz);
}

/**
 * Normalize a 2D vector
 */
function normalize2D(v) {
  const mag = Math.sqrt(v.x * v.x + v.z * v.z);
  if (mag === 0) return { x: 0, z: 0 };
  return { x: v.x / mag, z: v.z / mag };
}

/**
 * Calculate perpendicular distance from point to line segment
 */
function pointToLineSegmentDistance(point, lineStart, lineEnd) {
  const dx = lineEnd.x - lineStart.x;
  const dz = lineEnd.z - lineStart.z;
  const lengthSquared = dx * dx + dz * dz;

  if (lengthSquared === 0) {
    return distance2D(point, lineStart);
  }

  // Parameter t determines where on the line segment the closest point is
  let t =
    ((point.x - lineStart.x) * dx + (point.z - lineStart.z) * dz) /
    lengthSquared;
  t = Math.max(0, Math.min(1, t));

  const closestPoint = {
    x: lineStart.x + t * dx,
    z: lineStart.z + t * dz,
  };

  return distance2D(point, closestPoint);
}

/**
 * Check if two line segments overlap (are collinear and share some portion)
 */
function segmentsOverlap(edge, wall, tolerance = 0.05) {
  // Check if edge midpoint is close to wall line
  const edgeMidpoint = {
    x: (edge.start.x + edge.end.x) / 2,
    z: (edge.start.z + edge.end.z) / 2,
  };

  const distToWall = pointToLineSegmentDistance(
    edgeMidpoint,
    wall.start,
    wall.end
  );

  if (distToWall > tolerance) return false;

  // Project edge endpoints onto wall direction
  const wallVector = {
    x: wall.end.x - wall.start.x,
    z: wall.end.z - wall.start.z,
  };
  const wallLength = Math.sqrt(
    wallVector.x * wallVector.x + wallVector.z * wallVector.z
  );
  const wallDir = {
    x: wallVector.x / wallLength,
    z: wallVector.z / wallLength,
  };

  // Project edge start and end onto wall
  const edgeStartProj =
    (edge.start.x - wall.start.x) * wallDir.x +
    (edge.start.z - wall.start.z) * wallDir.z;
  const edgeEndProj =
    (edge.end.x - wall.start.x) * wallDir.x +
    (edge.end.z - wall.start.z) * wallDir.z;

  const edgeMin = Math.min(edgeStartProj, edgeEndProj);
  const edgeMax = Math.max(edgeStartProj, edgeEndProj);

  // Check if projected edge overlaps with wall segment [0, wallLength]
  return edgeMax >= -tolerance && edgeMin <= wallLength + tolerance;
}

/**
 * Check if shower edge aligns with a wall
 */
function isEdgeAlignedWithWall(edge, wall, tolerance = 0.05) {
  // 1. Check if edge is parallel to wall
  const edgeVector = normalize2D({
    x: edge.end.x - edge.start.x,
    z: edge.end.z - edge.start.z,
  });

  const wallVector = normalize2D({
    x: wall.end.x - wall.start.x,
    z: wall.end.z - wall.start.z,
  });

  const dotProduct = Math.abs(
    edgeVector.x * wallVector.x + edgeVector.z * wallVector.z
  );
  const isParallel = dotProduct > 0.98; // ~11 degrees tolerance

  if (!isParallel) return false;

  // 2. Check if edge is close to wall (perpendicular distance)
  const distToWall = pointToLineSegmentDistance(
    edge.start,
    wall.start,
    wall.end
  );
  const isClose = distToWall < tolerance; // 5cm tolerance

  if (!isClose) return false;

  // 3. Check if edge overlaps with wall segment
  const overlaps = segmentsOverlap(edge, wall, tolerance);

  return overlaps;
}

/**
 * Get wall polygon corners as line segments
 * Prefers room.walls[] data to match bathroom analyzer coordinate space
 */
function getWallSegments(room) {
  // PREFERRED: Use room.walls[] data directly (same source as bathroom analyzer)
  // This ensures shower corners and room walls are in the same coordinate space
  if (room.walls && room.walls.length > 0) {
    const segments = [];

    room.walls.forEach((wall, index) => {
      const center = {
        x: wall.center.x,
        z: wall.center.z,
      };

      const dimensions = {
        x: wall.dimensions.x,
        z: wall.dimensions.z,
      };

      // Calculate wall rotation to get direction vectors
      const rotationY = wall.rotation?.y || 0;
      const wallDir = {
        x: Math.cos(rotationY),
        z: Math.sin(rotationY),
      };

      // Calculate wall endpoints
      const halfLength = dimensions.x / 2;
      const start = {
        x: center.x - wallDir.x * halfLength,
        z: center.z - wallDir.z * halfLength,
      };
      const end = {
        x: center.x + wallDir.x * halfLength,
        z: center.z + wallDir.z * halfLength,
      };

      segments.push({
        start,
        end,
        wallIndex: index,
        wallId: wall.id,
      });
    });

    return segments;
  }

  // FALLBACK: Use floor polygon corners (legacy behavior)
  if (!room.floors?.[0]?.polygonCorners) {
    return [];
  }

  const floor = room.floors[0];
  const corners = transformPolygonCorners(
    floor.polygonCorners,
    floor.transform
  );

  const segments = [];
  for (let i = 0; i < corners.length; i++) {
    const start = corners[i];
    const end = corners[(i + 1) % corners.length];
    segments.push({
      start: { x: start.x, z: start.z },
      end: { x: end.x, z: end.z },
      wallIndex: i,
    });
  }

  return segments;
}

/**
 * Calculate shower wall tiling areas
 * Works for any shower type: corner, alcove, single-wall, or freestanding
 *
 * @param {Array} showerFootprint - 4 corners of shower base [p0, p1, p2, p3]
 * @param {Object} room - Room object with walls data
 * @param {number} tilingHeight - Height to tile in meters (default 2.4m for shower code compliance)
 * @returns {Object} Tiling information for each side
 */
export function calculateShowerWallTiling(
  showerFootprint,
  room,
  tilingHeight = 2.4
) {
  if (!showerFootprint || showerFootprint.length < 4) {
    return {
      sides: [],
      totalArea: 0,
      tiledSideCount: 0,
      showerType: 'invalid',
    };
  }

  const tilingSides = [];

  // Get the 4 edges of the shower footprint
  const edges = [
    { start: showerFootprint[0], end: showerFootprint[1], index: 0 },
    { start: showerFootprint[1], end: showerFootprint[2], index: 1 },
    { start: showerFootprint[2], end: showerFootprint[3], index: 2 },
    { start: showerFootprint[3], end: showerFootprint[0], index: 3 },
  ];

  // Get wall segments from room
  const walls = getWallSegments(room);

  // For each edge, check if it aligns with a wall
  edges.forEach((edge) => {
    const edgeLength = distance2D(edge.start, edge.end);

    // Check against all room walls
    walls.forEach((wall) => {
      const aligned = isEdgeAlignedWithWall(edge, wall);
      if (aligned) {
        // Calculate wall angle (normal direction facing into room)
        // Wall direction is from start to end
        const wallDx = wall.end.x - wall.start.x;
        const wallDz = wall.end.z - wall.start.z;
        // Normal is perpendicular to wall direction (rotate 90° CCW)
        // Normal angle = atan2(-wallDz, wallDx) or equivalently atan2(wallDx, wallDz) + π/2
        const wallAngle = Math.atan2(wallDx, wallDz);

        tilingSides.push({
          edgeIndex: edge.index,
          wallIndex: wall.wallIndex,
          wallId: wall.wallId,
          length: edgeLength,
          height: tilingHeight,
          area: edgeLength * tilingHeight,
          startPoint: edge.start,
          endPoint: edge.end,
          wallAngle: wallAngle, // Direction along the wall (for rotation calculation)
        });
      }
    });
  });

  // Determine shower type based on number of tiled sides
  let showerType;
  switch (tilingSides.length) {
    case 0:
      showerType = 'freestanding';
      break;
    case 1:
      showerType = 'single-wall';
      break;
    case 2:
      showerType = 'corner';
      break;
    case 3:
      showerType = 'alcove';
      break;
    case 4:
      showerType = 'enclosed';
      break;
    default:
      showerType = 'unknown';
  }

  const totalArea = tilingSides.reduce((sum, side) => sum + side.area, 0);

  return {
    sides: tilingSides,
    totalArea,
    tiledSideCount: tilingSides.length,
    showerType,
    tilingHeight,
  };
}

/**
 * Determines the handedness (left or right) of a corner shower based on wall alignment
 *
 * Corner shower edge convention (from physicalCorners):
 *    Corner 0 ──────── Corner 1
 *       │                  │
 *  Edge 3              Edge 1
 *       │                  │
 *    Corner 3 ──────── Corner 2
 *           Edge 2
 *
 * Edge indices:
 * - Edge 0: Top (corner 0 → 1)
 * - Edge 1: Right (corner 1 → 2)
 * - Edge 2: Bottom (corner 2 → 3)
 * - Edge 3: Left (corner 3 → 0)
 *
 * Handedness:
 * - 'left': Back wall + left wall (edges include 3 or combination that suggests left corner)
 * - 'right': Back wall + right wall (edges include 1 or combination that suggests right corner)
 *
 * @param {Array} tilingSides - Array of aligned wall sides from calculateShowerWallTiling
 * @returns {string} 'left' or 'right'
 */
export function determineCornerHandedness(tilingSides) {
  if (!tilingSides || tilingSides.length !== 2) {
    console.warn(
      'determineCornerHandedness: Expected 2 tiling sides for corner shower, defaulting to left'
    );
    return 'left';
  }

  // Get the two edge indices that align with walls
  const edgeIndices = tilingSides.map((side) => side.edgeIndex).sort();
  const edgeKey = edgeIndices.join(',');

  // Common corner configurations:
  // Left corner: edges [0,3] (top + left) or [2,3] (bottom + left)
  // Right corner: edges [0,1] (top + right) or [1,2] (right + bottom)
  switch (edgeKey) {
    case '0,3': // Top + Left
    case '2,3': // Bottom + Left
      return 'left';

    case '0,1': // Top + Right
    case '1,2': // Right + Bottom
      return 'right';

    default:
      return 'left';
  }
}

/**
 * Calculates the rotation (Y-axis) for a corner shower to align with walls
 *
 * Uses the direction of Edge 0 (physicalCorners[0] → physicalCorners[1])
 * which is always along the wallVector direction.
 *
 * At rotation.y = 0, the procedural geometry has Edge 0 along +X (angle = π/2).
 * So: rotation = edge0Direction - π/2
 *
 * @param {Array} tilingSides - Array of aligned edges (unused, kept for API compatibility)
 * @param {Array} corners - Physical corners of the shower [C0, C1, C2, C3]
 * @param {string} handedness - 'left' or 'right' (unused for rotation, only affects geometry)
 * @returns {number} - Rotation in radians for shower.rotation.y
 */
export function calculateCornerShowerRotation(
  tilingSides,
  corners,
  handedness
) {
  if (!corners || corners.length < 2) {
    console.warn('calculateCornerShowerRotation: Invalid corners, returning 0');
    return 0;
  }

  // Compute direction of Edge 0 (from Corner 0 to Corner 1)
  // This is always along the wallVector direction
  const edge0Dx = corners[1].x - corners[0].x;
  const edge0Dz = corners[1].z - corners[0].z;
  const edge0Direction = Math.atan2(edge0Dx, edge0Dz);

  // At rotation.y = 0, procedural Edge 0 (back) runs along +X
  // Direction along +X: atan2(1, 0) = π/2
  const proceduralEdge0Direction = Math.PI / 2;

  // Rotation needed to align physical Edge 0 with procedural Edge 0
  const rotation = edge0Direction - proceduralEdge0Direction;

  return rotation;
}
